/* use client */
import { integrationDataAtom } from '@/atoms/integrationAtoms';
import { useMutateUpdateSessionEHRDetails } from '@/hooks/mutation/integration/useMutateUpdateSessionEHRDetails';
import { useMutateCreateNewSession } from '@/hooks/mutation/useMutateCreateNewSession';
import { useMutateUpdatePatientDetailsFromSession } from '@/hooks/mutation/useMutateUpdatePatientDetailsFromSession';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { type MedicalDirectorSidebar } from '@/integrations/medical-director/medicalDirectorSidebar';
import { userHasMedicalDirector } from '@/lib/analytics/scribe/medical-director';
import { type RootState } from '@/store';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';
import { useAtomValue } from 'jotai';
import { memo, type ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMdAuthPersistence } from './hooks/useMdAuthPersistence';
import { useMdCallbacks } from './hooks/useMdCallbacks';
import { useMdPatientSync } from './hooks/useMdPatientSync';
import { useMdSessionSync } from './hooks/useMdSessionSync';
import { useMdSidebar } from './hooks/useMdSidebar';
import { usePendingMdPatient } from './hooks/usePendingMdPatient';
import { type MedicalDirectorPractitionerDetails } from './types';

export const MedicalDirector = memo((): ReactNode => {
  const { data: kindeUserId } = useQueryUser((data) => data.kinde_user_id);
  const sessionId = useCurrentSessionId();

  const isRunning = useSelector((state: RootState) => state.timerModel.isRunning);
  const integrationData = useAtomValue(integrationDataAtom);

  const [practitionerDetails, setPractitionerDetails] =
    useState<MedicalDirectorPractitionerDetails | null>(null);
  const [medicalDirector, setMedicalDirector] = useState<MedicalDirectorSidebar | null>(
    null
  );

  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => ({
      session_id: data?.session_id,
      is_consent: data?.is_consent,
      patient: data?.patient,
      consult_note: data?.consult_note,
      ehr_patient_id: data?.ehr_patient_id,
    }),
  });

  const { mutateAsync: createSession, isPending: isCreatingSession } =
    useMutateCreateNewSession();
  const { mutate: updatePatientDetails, isPending: isUpdatingPatientDetails } =
    useMutateUpdatePatientDetailsFromSession();
  const { mutate: updateSessionEHRDetails, isPending: isUpdatingSessionEHRDetails } =
    useMutateUpdateSessionEHRDetails();

  useMdAuthPersistence();
  const pendingPatient = usePendingMdPatient();

  const callbacks = useMdCallbacks({
    medicalDirector,
    pendingPatient,
    session,
    practitionerDetails,
    setPractitionerDetails,
    isRunning,
    isUpdatingPatientDetails,
    integrationData,
    updatePatientDetails,
  });

  useMdSidebar({
    medicalDirector,
    setMedicalDirector,
    processDemographicsResponse: callbacks.processDemographicsResponse,
    processPractitionerResponse: callbacks.processPractitionerResponse,
    integrationCallbacks: {
      onPushNotes: callbacks.onPushNotesCallback,
      onSwitchPatient: callbacks.onSwitchPatientCallback,
      onCheckCurrentPatient: callbacks.onCheckCurrentPatientCallback,
    },
    mdEventHandler: callbacks.mdEventHandler,
  });

  useMdSessionSync({
    medicalDirector,
    pendingPatient,
    sessionId,
    session,
    integrationPatientId: integrationData.patientId,
    isCreatingSession,
    isUpdatingPatientDetails,
    isUpdatingSessionEHRDetails,
    createSession,
  });

  useMdPatientSync({
    medicalDirector,
    pendingPatient,
    sessionId,
    session,
    integrationPatientId: integrationData.patientId,
    isUpdatingPatientDetails,
    isUpdatingSessionEHRDetails,
    updateSessionEHRDetails,
    onSwitchPatient: callbacks.onSwitchPatientCallback,
  });

  useEffect(() => {
    if (kindeUserId && medicalDirector) {
      userHasMedicalDirector({
        authID: kindeUserId,
        userTraits: {
          ehr_provider: EHRProvider.Medical_Director,
          from: 'medicalDirector',
        },
      });
    }
  }, [kindeUserId, medicalDirector]);

  return <></>;
});

MedicalDirector.displayName = 'MedicalDirector';
